import { useEffect, useRef } from 'react'

import { pickScriptScrollTarget } from '@/features/studio/script/scroll-spy'
import type { ScriptScrollTarget } from '@/features/studio/script/scroll-spy'

/** 中间编辑区滚动时，计算当前应高亮的集/场 */
export function useScriptScrollSpy(opts: {
  rootRef: React.RefObject<HTMLElement | null>
  enabled: boolean
  markerVersion: string
  pausedRef: React.RefObject<boolean>
  onTarget: (target: ScriptScrollTarget) => void
}) {
  const { rootRef, enabled, markerVersion, pausedRef, onTarget } = opts
  const onTargetRef = useRef(onTarget)
  onTargetRef.current = onTarget

  useEffect(() => {
    if (!enabled) return

    let frame = 0
    let root: HTMLElement | null = null
    let mo: MutationObserver | null = null

    const pick = () => {
      frame = 0
      if (pausedRef.current) return
      const el = root ?? rootRef.current
      if (!el) return
      root = el
      const target = pickScriptScrollTarget(el)
      if (target) onTargetRef.current(target)
    }

    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(pick)
    }

    const attach = (el: HTMLElement) => {
      root = el
      el.addEventListener('scroll', schedule, { passive: true })
      mo = new MutationObserver(schedule)
      mo.observe(el, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-scroll-marker'],
      })
      schedule()
    }

    const current = rootRef.current
    if (current) attach(current)

    const retry = window.setInterval(() => {
      if (root) {
        window.clearInterval(retry)
        return
      }
      const el = rootRef.current
      if (el) {
        window.clearInterval(retry)
        attach(el)
      }
    }, 80)

    return () => {
      window.clearInterval(retry)
      if (frame) cancelAnimationFrame(frame)
      mo?.disconnect()
      if (root) root.removeEventListener('scroll', schedule)
      root = null
    }
  }, [enabled, markerVersion, pausedRef, rootRef])
}
